import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query'
import { getLocalStorageItem } from '../utils/local-storage.ts'
import { sendErrorMsgToTeams } from '../utils/send-error-message-to-teams.ts'
import { UserInfo } from './auth/auth.def.ts'
import { jwtDecode } from 'jwt-decode'
import { NETWORK_ERROR_MESSAGE } from '../redux/utils.ts'
import { newRelic } from '../utils/new-relic.ts'
import { notifyError } from '../utils/global-notification.ts'

export function getBaseUrl(interfaceName: string): string {
  const endpoint = getIdeyaBaseUrl()
  return endpoint + interfaceName
}

export function getIdeyaBaseUrl(): string {
  const ideyaApiUrl = import.meta.env.VITE_IDEYA_API_URL as string
  const ideyaInternalApiUrl = import.meta.env
    .VITE_IDEYA_INTERNAL_API_URL as string

  const internalRegex = /^internal(-[a-zA-Z0-9]+)?\.polymerideya\.com$/

  // Dev mode
  if (internalRegex.test(window.location.hostname) && ideyaInternalApiUrl) {
    return ideyaInternalApiUrl
  }

  return ideyaApiUrl
}

// Opt-in per API slice. Requests that hang longer than this are treated as
// unreachable rather than left spinning forever.
export const REQUEST_TIMEOUT_MS = 30_000

// A page load fires many requests at once; a single outage would otherwise
// stack one notification per request.
const NETWORK_ERROR_COOLDOWN_MS = 60_000
let lastNetworkErrorNotifiedAt = 0

function notifyNetworkError() {
  const now = Date.now()
  if (now - lastNetworkErrorNotifiedAt < NETWORK_ERROR_COOLDOWN_MS) return
  lastNetworkErrorNotifiedAt = now
  notifyError('Error', NETWORK_ERROR_MESSAGE)
}

export function getBaseQuery(
  baseUrl: string,
  timeout?: number
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> {
  return async (args, api, extraOptions) => {
    const baseQuery = fetchBaseQuery({
      baseUrl: baseUrl,
      credentials: 'include',
      timeout,
      prepareHeaders: (headers) => {
        const token = getLocalStorageItem('apiAccessToken')
        if (token) {
          headers.set('Authorization', `Bearer ${token}`)
        }
        headers.set('Content-Type', 'application/json')
        return headers
      },
    })

    // ag-grid datasources call this outside RTK Query and pass `{}` as the api
    // object, so `api.signal` is undefined. With `timeout` set, fetchBaseQuery
    // reads `.aborted` off that signal and throws before its own error
    // handling runs — so supply one.
    if (timeout && !api?.signal) {
      api = { ...api, signal: new AbortController().signal } as typeof api
    }

    const result = await baseQuery(args, api, extraOptions)
    if (result.error) {
      // No response at all: offline, DNS/CORS failure, or server unreachable.
      // Nothing here implies an expired token, so don't claim it does.
      if (
        result.error.status === 'FETCH_ERROR' ||
        result.error.status === 'TIMEOUT_ERROR'
      ) {
        // A request the caller cancelled also lands in FETCH_ERROR, since RTK
        // labels every non-TimeoutError throw that way: an unmounted component
        // or a superseded query, not a network problem. StrictMode's
        // double-mount makes it fire on every dev page load. TIMEOUT_ERROR is
        // an abort too, so key off the status rather than the error text.
        const wasCancelled =
          result.error.status === 'FETCH_ERROR' &&
          result.error.error?.includes('AbortError')

        if (!wasCancelled) {
          notifyNetworkError()
        }
        return result
      }

      let userInfo: UserInfo | null = null
      const token = getLocalStorageItem('apiAccessToken')
      try {
        userInfo = jwtDecode(token) as UserInfo
      } catch {
        // Missing or corrupt token. Record before navigating away, since
        // location.href ends the replay.
        newRelic.noticeError(new Error('stored token could not be decoded'), {
          context: 'apiUtilTokenDecodeFailed',
        })
        window.location.href = '/logout'
        return result
      }

      const { fullName, email, firmRole, groupRole, role } = userInfo || {}

      const message = (result.error.data as { message?: string })?.message
      notifyError(
        `Error: ${result.error.status}`,
        message !== '' && message ? message : 'An error occurred'
      )

      sendErrorMsgToTeams(
        `${fullName} (${email})`,
        [firmRole, groupRole, role],
        `Status: ${result.error.status}\n
        Message: ${message}\n
        Method: ${result.meta?.request.method}\n
        Url: ${result.meta?.request.url}\n
        Body: ${
          typeof args !== 'string'
            ? args.body != null
              ? JSON.stringify(args.body)
              : null
            : args
        }\n`
      )

      return result
    }
    return result
  }
}
